import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ClipboardList, Download, Search, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

export default function AttendanceReports() {
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/classes').then(r => setClasses(r.data)).catch(() => {});
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClass) params.set('class_id', filterClass);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await api.get(`/admin/attendance/report?${params}`);
      setReport(data);
    } catch {
      toast.error('Failed to load report');
    } finally { setLoading(false); }
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (filterClass) params.set('class_id', filterClass);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('export', 'csv');
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/attendance/report?${params}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'attendance_report.csv';
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('CSV exported');
      })
      .catch(() => toast.error('Export failed'));
  };

  const statusColors = { present: 'status-present', absent: 'status-absent', late: 'status-late', excused: 'status-excused' };

  return (
    <div data-testid="attendance-reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><ClipboardList className="w-6 h-6" /> Attendance Reports</h1>
        {report && (
          <Button onClick={exportCSV} variant="outline" className="border-[#1B2A4A] text-[#1B2A4A]" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label>Class</Label>
              <Select value={filterClass || 'all'} onValueChange={v => setFilterClass(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="report-class-filter"><SelectValue placeholder="All Classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From Date</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="report-date-from" />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="report-date-to" />
            </div>
            <Button onClick={loadReport} disabled={loading} className="bg-[#1B2A4A]" data-testid="generate-report-btn">
              <Search className="w-4 h-4 mr-1" /> {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {report && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Total Records</p><p className="text-2xl font-black text-[#1B2A4A]">{report.summary.total}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Present</p><p className="text-2xl font-black text-emerald-600">{report.summary.present}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Absent</p><p className="text-2xl font-black text-red-600">{report.summary.absent}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Late</p><p className="text-2xl font-black text-amber-600">{report.summary.late}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Attendance Rate</p><p className="text-2xl font-black text-[#C9A84C]">{report.summary.percentage}%</p></div>
          </div>

          {/* Records Table */}
          <div className="bg-white rounded-xl border shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="data-table">
                  <TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Grade</TableHead><TableHead>Class</TableHead><TableHead>Status</TableHead><TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.records.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No records found</TableCell></TableRow>
                ) : report.records.slice(0, 200).map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.date}</TableCell>
                    <TableCell className="font-semibold">{r.student_name}</TableCell>
                    <TableCell>{r.student_grade}</TableCell>
                    <TableCell>{r.class_name}</TableCell>
                    <TableCell><span className={`${statusColors[r.status] || ''} text-xs px-2 py-1 rounded-full font-semibold capitalize`}>{r.status}</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{r.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {report.records.length > 200 && (
              <p className="text-sm text-slate-400 p-4 text-center">Showing first 200 of {report.records.length} records. Export CSV for full data.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
